import { tileDataProp } from 'components/atoms/TileButton';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Legend from 'components/molecules/Legend';
import Linechart from 'components/molecules/Linechart';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import {
    graphItemContainerClass,
    subHeaderClass,
    yAxisLabelClass,
} from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchSalesTrendChart } from 'hooks/growthAnalysis/useFetchSalesTrendChart';
import _ from 'lodash';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Select } from 'antd';
import tw from 'twin.macro';
import DownSvg from 'assets/svg/Down';
import { trendChartDownload } from 'parser/graphToExcelDownloadParser';
import {
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    formatUnit,
    getAdditionalDataOnCopyClick,
    getConfig,
} from 'utils/helperMethods';

export const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

const currentYearSalesLegends = [
    { label: 'Year Ago', color: colorConstants.lightBlue },
    { label: 'Current Year', color: colorConstants.darkOrange },
    { label: 'Positive', color: colorConstants.limeGreen },
    { label: 'Negative', color: colorConstants.red },
];
const currentYearSalesHeader = 'vs YA';

const percentageChangeTitle = '% Change';

const barWidthMap: any = {
    4: 30,
    12: 20,
    26: 20,
    52: 11,
};

type Props = {
    index: number;
    stringifiedFilter: string;
    volTile: tileDataProp;
    absTile: tileDataProp;
    loading: boolean;
    label: string;
    selectPPG: any;
    chart2Item: any;
    chart1Item: any;
};

const chartHeight = calculateFontSize(window.innerWidth, 130);
const yAxisWidth = calculateFontSize(window.innerWidth, 80);

const TrendChart: FC<Props> = ({
    index,
    stringifiedFilter,
    loading,
    absTile,
    volTile,
    label,
    selectPPG,
    chart2Item,
    chart1Item,
}) => {
    const {
        data: salesTrendData,
        loading: salesTrendLoading,
        error: salesTrendError,
        setLoading: trendsChartSetLoading,
        fetchTrendsChartData,
    } = useFetchSalesTrendChart();

    const {
        loading: applyFilterLoading,
        error: applyFilterError,
        applyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete, onError: onApplyFilterError });
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const decimalForPercentage = commonConfig?.decimalForPercentage ?? 2;
    const decimalForNonPercentage = commonConfig?.decimalForNonPercentage ?? 2;
    const { trendChartDropdownData } = growthAnalysisConfig;
    const [growthAnalysisData, setGrowthAnalysisData] = useState(trendChartDropdownData[0]);
    const salesAttributionDrillDownChartKeyValueMapping =
        growthAnalysisConfig.salesAttributionDrillDownChartKeyValueMapping || {};
    const { timePeriodSelection, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    const trendsChartRef = useRef<any>();

    const { lineChartResult = null, barChartResult = null } = salesTrendData || {};
    const barWidth = barWidthMap[lineChartResult?.data?.length] || 11;

    const containerWidth = useContainerWidth({
        containerId: `trends-chart-growth-analysis`,
        buffer: yAxisWidth + 34 * 2,
    }); //subtracting graph width and padding on 2 sides

    useEffect(() => {
        if (chart1Item && chart2Item) {
            prepareApplyFilterVariableAndExecute();
        }
    }, [chart1Item, chart2Item, volTile, timePeriodSelection]);

    const combinedError = salesTrendError || applyFilterError;
    const combinedLoading = loading || salesTrendLoading;
    const currency = volTile?.key === 'val' ? fetchCurrency(appConfig) : '';
    function prepareApplyFilterVariableAndExecute() {
        trendsChartSetLoading(true);
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        if (selectPPG.dimension === 'dim1') {
            tempApplyFilterVariable.jsonstring_dim1.inclusion = [
                {
                    attributeExId: chart2Item?.category,
                    attributeDesc: chart2Item?.category,
                    dimension: 'dim1',
                    attributeType: _.snakeCase(selectPPG.label),
                },
            ];
        } else {
            tempApplyFilterVariable.jsonstring_dim2.inclusion = [
                {
                    attributeExId: chart2Item?.category,
                    attributeDesc: chart2Item?.category,
                    dimension: 'dim2',
                    attributeType: _.snakeCase(selectPPG.label),
                },
            ];
        }
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        applyFilterMutate({ variables: tempApplyFilterVariable });
    }

    function onApplyFilterError() {
        trendsChartSetLoading(false);
    }

    //called when apply filter call has completed
    function onApplyFilterComplete(payload: any) {
        const idForRestOfComponent = JSON.parse(stringifiedFilter);
        const tempApplyFilterResponse = _.cloneDeep(idForRestOfComponent);
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (idForRestOfComponent.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(idForRestOfComponent.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (idForRestOfComponent.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(idForRestOfComponent.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        const stringifiedApplyFilterResponse = JSON.stringify(tempApplyFilterResponse);
        fetchTrendsChart(stringifiedApplyFilterResponse);
    }

    function fetchTrendsChart(stringifiedFilter: string) {
        fetchTrendsChartData({
            variables: {
                attributionField:
                    chart1Item?.category in salesAttributionDrillDownChartKeyValueMapping
                        ? salesAttributionDrillDownChartKeyValueMapping[chart1Item?.category]
                        : _.snakeCase(chart1Item?.category),
                dimension: selectPPG?.dimension,
                attributeType: _.snakeCase(selectPPG?.label),
                timePeriod: String(timePeriodSelection).toLowerCase(),
                contributionType: String(volTile?.dataKey)?.toLowerCase(),
                filters: stringifiedFilter,
                limit: 52,
                offset: 0,
            },
        });
    }
    const trendsChartExportData = useMemo(() => {
        return trendChartDownload(lineChartResult?.data, barChartResult?.data);
    }, [lineChartResult?.data, barChartResult?.data]);

    // IF ERROR RETURN ERROR MESSAGE
    if (combinedError && !combinedLoading && !salesTrendData) return <ErrorBox msg={String(salesTrendError)} />;

    // IF LOADING RETURN LOADER
    if (combinedLoading)
        return (
            <div style={{ height: 370, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );
    const selectedAttributeLabel = label;
    return (
        <div ref={trendsChartRef} className={`${graphItemContainerClass} mt-4`} id="trends-chart-growth-analysis">
            <div className="flex items-center justify-between">
                <p className={subHeaderClass}>
                    {selectedAttributeLabel} {currentYearSalesHeader}
                </p>
                <div className="flex items-center justify-end gap-2">
                    {/* <CustomSelect
                        style={{ marginRight: 16 }}
                        suffixIcon={<DownSvg />}
                        disabled={false}
                        value={growthAnalysisData.key}
                        options={trendChartDropdownData}
                        className="dropdown-height dropdown-width"
                        onChange={(val: any, item: any) => {
                            setGrowthAnalysisData(item);
                        }}
                    /> */}
                    <CopyToClipBoard refOfComponent={trendsChartRef} />
                    <DownloadExcel
                        title={'TrendChart'}
                        excelData={trendsChartExportData.data}
                        headers={trendsChartExportData.headers}
                    />
                </div>
            </div>

            <div className=" my-1">
                <p className={`${yAxisLabelClass} relative mt-[-16px] top-5`}>CY vs YA</p>
                <Legend data={currentYearSalesLegends} />
            </div>
            <div className={`my-2 ${yAxisLabelClass}`}>
                <p>
                    Sales {volTile.key === 'units' ? 'Units' : ''}
                    {formatUnit(`${absTile.key}_${volTile.key}`, true, appConfig)}
                </p>
            </div>
            <div>
                <div className="ml-1 flex pb-5">
                    <Linechart
                        padding={[0, 0, 12, 0]}
                        // yAxisConfig={false}
                        xAxisConfig={false}
                        YGrid={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-line-y-${index}`}
                        fields={['period', 'Year Ago', 'Current Year']}
                        minBound={lineChartResult?.minVal}
                        maxBound={lineChartResult?.maxVal}
                        width={yAxisWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => {
                            return val;
                        }}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                        }}
                        groupedChartColor={[colorConstants.lightBlue, colorConstants.darkOrange]}
                        colorKey={'color'}
                        disableIntervalLabel
                    />
                    <Linechart
                        // yAxisConfig={false}
                        dataset={lineChartResult?.data || []}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-line-${index}`}
                        fields={['period', 'Year Ago', 'Current Year']}
                        minBound={lineChartResult?.minVal}
                        maxBound={lineChartResult?.maxVal}
                        width={containerWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => {
                            return val;
                        }}
                        groupedChartColor={[colorConstants.lightBlue, colorConstants.darkOrange]}
                        colorKey={'color'}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis, val, from) => {
                            return {
                                name: from,
                                value:
                                    currency +
                                    formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                            };
                        }}
                    />
                </div>
                <div className="my-5">
                    <p className={`${yAxisLabelClass} !top-0`}>{selectedAttributeLabel}</p>
                    <p className={`${yAxisLabelClass} relative !top-2`}>{percentageChangeTitle}</p>
                </div>

                <div className="ml-1 flex">
                    <Barchart
                        padding={[0, 0, 12, 0]}
                        // yAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-column-y-${index}`}
                        fields={['category', 'sales']}
                        minBound={barChartResult?.minVal}
                        maxBound={barChartResult?.maxVal}
                        width={yAxisWidth} //subtracting the padding
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMBAlt(val, decimalForPercentage) + '%';
                        }}
                        xAxisConfig={false}
                        YGrid={false}
                        colorKey={'color'}
                        disableIntervalLabel
                    />
                    <Barchart
                        barWidth={barWidth}
                        // yAxisConfig={false}
                        dataset={barChartResult?.data || []}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`current-year-sales-column-${index}`}
                        fields={['category', 'sales']}
                        minBound={barChartResult?.minVal}
                        maxBound={barChartResult?.maxVal}
                        width={containerWidth} //subtracting the padding
                        xAxisLabelFormatter={(val: any) => {
                            return val;
                        }}
                        intervalStyle={{ radius: [6, 6, 0, 0] }}
                        colorKey={'color'}
                        disableIntervalLabel
                        tooltipFormatter={(xAxis, val) => {
                            return {
                                name: percentageChangeTitle,
                                value: formatNumWithKMBAlt(val, decimalForPercentage) + '%',
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TrendChart;
